// Página de perfil completo de un político nicoleño.
// Diseño: hero (foto/nombre/cargo/badges) + pestañas
// (Resumen · Investigaciones · Noticias · HCD · Red · Chat IA).
// Agrega TODO lo relacionado: perfil HCD, dossiers, noticias (media_articles),
// HCD relacionado (búsqueda híbrida), denuncias/conflictos/redes, conexiones y chat.
// Ruta: /investigations/politico/:slug
import React, { useEffect, useMemo, useRef, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  FaUserCircle,
  FaArrowLeft,
  FaExclamationTriangle,
  FaExternalLinkAlt,
  FaRobot,
  FaUser,
  FaPaperPlane,
  FaTrashAlt,
  FaBalanceScale,
  FaBuilding,
  FaNewspaper,
  FaLandmark,
  FaProjectDiagram,
  FaIdBadge,
  FaUsers,
  FaRegClock,
  FaLayerGroup,
  FaBrain,
  FaSyncAlt,
} from 'react-icons/fa';
import {
  Loading,
  ErrorBox,
  Empty,
  borderSoft,
  panelDeep,
  mono,
  accent,
} from '../../styles/techComponents';
import {
  getPolitician,
  getPoliticianChatHistory,
  sendPoliticianChat,
  clearPoliticianChatHistory,
  getPoliticianSynthesis,
  generatePoliticianSynthesis,
} from '../../services/investigationService';

/* ------------------------------- styles ------------------------------- */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-bottom: 2rem;
`;

const Back = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: transparent;
  border: 1px solid ${borderSoft};
  color: var(--text-secondary);
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  font-size: 0.78rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(0, 180, 216, 0.45); color: var(--text-primary); }
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  gap: 1.3rem;
  align-items: center;
  padding: 1.5rem 1.6rem;
  background:
    radial-gradient(1200px 200px at 0% 0%, rgba(0, 180, 216, 0.10), transparent 60%),
    ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 14px;
  overflow: hidden;

  .photo {
    width: 120px; height: 120px;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(11, 19, 32, 0.7);
    border: 2px solid rgba(0, 180, 216, 0.4);
    flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    color: var(--text-disabled);
    font-size: 3rem;
    img { width: 100%; height: 100%; object-fit: cover; }
  }
  .meta { flex: 1; min-width: 0; }
  h1 {
    margin: 0 0 4px;
    color: var(--text-primary);
    font-size: 1.7rem;
    line-height: 1.15;
  }
  .role {
    font-family: ${mono};
    font-size: 0.68rem;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    color: ${accent};
    margin-bottom: 3px;
  }
  .party { color: var(--text-secondary); font-size: 0.95rem; }
  .stats {
    display: flex; gap: 0.6rem; flex-wrap: wrap; margin-top: 0.9rem;
  }
  .stat {
    display: flex; flex-direction: column; align-items: flex-start;
    padding: 0.4rem 0.8rem;
    border: 1px solid ${borderSoft};
    border-radius: 8px;
    background: rgba(11, 19, 32, 0.45);
    min-width: 74px;
    .n { color: var(--text-primary); font-size: 1.15rem; font-weight: 700; line-height: 1; }
    .l {
      font-family: ${mono}; font-size: 0.55rem; letter-spacing: 0.13em;
      text-transform: uppercase; color: var(--text-disabled); margin-top: 4px;
    }
    &.warn { border-color: rgba(244, 162, 97, 0.4); background: rgba(244, 162, 97, 0.08); .n { color: #f4a261; } }
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  border-bottom: 1px solid ${borderSoft};
  padding-bottom: 2px;
`;
const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: ${(p) => (p.$active ? 'rgba(0, 180, 216, 0.12)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--text-primary)' : 'var(--text-secondary)')};
  border: 1px solid ${(p) => (p.$active ? 'rgba(0, 180, 216, 0.4)' : 'transparent')};
  border-bottom: none;
  padding: 0.55rem 0.95rem;
  border-radius: 8px 8px 0 0;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover { color: var(--text-primary); }
  .count {
    font-family: ${mono};
    font-size: 0.62rem;
    padding: 1px 6px;
    border-radius: 999px;
    background: rgba(0, 180, 216, 0.12);
    color: ${accent};
  }
`;

const Panel = styled.div`
  padding: 0.4rem 0.1rem;
`;

const SectionH = styled.div`
  font-family: ${mono};
  font-size: 0.64rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--text-disabled);
  margin: 1.1rem 0 0.6rem;
  display: flex; align-items: center; gap: 7px;
  &:first-child { margin-top: 0.2rem; }
`;

const Bio = styled.div`
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 8px;
  padding: 0.85rem 1rem;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.6;
`;

const CardLink = styled(Link)`
  display: flex; align-items: center; gap: 11px;
  padding: 0.7rem 0.9rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 9px;
  text-decoration: none;
  color: var(--text-secondary);
  margin-bottom: 7px;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(0, 180, 216, 0.45); transform: translateY(-1px); }
  .swatch { width: 10px; height: 10px; border-radius: 3px; flex-shrink: 0; }
  .tw { flex: 1; min-width: 0; }
  strong { color: var(--text-primary); display: block; font-size: 0.92rem; }
  .resume {
    font-size: 0.8rem; color: var(--text-disabled); margin-top: 2px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .badges { display: flex; gap: 4px; flex-shrink: 0; }
  .badge {
    font-family: ${mono}; font-size: 0.54rem; letter-spacing: 0.1em;
    padding: 2px 6px; border-radius: 4px; border: 1px solid;
  }
  .badge.warn { color: #f4a261; background: rgba(244, 162, 97, 0.08); border-color: rgba(244, 162, 97, 0.4); }
  .badge.conf { color: ${accent}; background: rgba(0, 180, 216, 0.06); border-color: rgba(0, 180, 216, 0.4); }
`;

const ExtLink = styled.a`
  display: flex; gap: 11px;
  padding: 0.75rem 0.9rem;
  background: ${panelDeep};
  border: 1px solid ${borderSoft};
  border-radius: 9px;
  text-decoration: none;
  margin-bottom: 7px;
  transition: all 0.15s ease;
  &:hover { border-color: rgba(0, 180, 216, 0.45); }
  .icon { color: ${accent}; font-size: 0.95rem; margin-top: 2px; flex-shrink: 0; }
  .tw { flex: 1; min-width: 0; }
  .title { color: var(--text-primary); font-size: 0.9rem; line-height: 1.35; font-weight: 600; }
  .sum {
    color: var(--text-disabled); font-size: 0.8rem; margin-top: 3px; line-height: 1.45;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
  }
  .src {
    display: flex; gap: 8px; align-items: center; margin-top: 5px;
    font-family: ${mono}; font-size: 0.58rem; letter-spacing: 0.1em;
    text-transform: uppercase; color: var(--text-secondary);
  }
  .ext { color: var(--text-disabled); flex-shrink: 0; }
`;

const ListUl = styled.ul`
  list-style: none; padding: 0; margin: 0 0 0.6rem;
`;
const Risk = styled.li`
  background: rgba(239, 71, 111, 0.05);
  border: 1px solid rgba(239, 71, 111, 0.25);
  border-radius: 8px;
  padding: 0.6rem 0.85rem;
  margin-bottom: 6px;
  font-size: 0.86rem; color: var(--text-secondary); line-height: 1.5;
  .meta {
    font-family: ${mono}; font-size: 0.56rem; letter-spacing: 0.12em;
    text-transform: uppercase; color: var(--text-disabled); margin-bottom: 3px;
  }
  &.soft { background: rgba(244, 162, 97, 0.06); border-color: rgba(244, 162, 97, 0.3); }
`;

const ConnRow = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 0.55rem 0.8rem;
  background: rgba(11, 19, 32, 0.45);
  border: 1px solid ${borderSoft};
  border-radius: 8px;
  margin-bottom: 6px;
  font-size: 0.86rem;
  .arr { color: var(--text-disabled); }
  .name { color: var(--text-primary); font-weight: 600; }
  .kind {
    font-family: ${mono}; font-size: 0.58rem; letter-spacing: 0.12em;
    text-transform: uppercase; padding: 2px 7px; border-radius: 5px; border: 1px solid;
  }
  .desc { color: var(--text-disabled); font-size: 0.78rem; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const KIND_COLOR = {
  family: '#ef476f', business: '#f4a261', political: '#00b4d8', employment: '#a78bfa',
  financial: '#06d6a0', legal: '#fbbf24', social: '#9ca3af', historical: '#7e7e7e',
  rivalry: '#ef4444', other: '#6b7280',
};

/* ------------------------------- chat -------------------------------- */

const ChatWrap = styled.div`
  display: flex; flex-direction: column; min-height: 0;
  border: 1px solid ${borderSoft};
  border-radius: 10px;
  background: rgba(11, 19, 32, 0.3);
  overflow: hidden;
  .head {
    display: flex; align-items: center; gap: 7px;
    padding: 0.6rem 0.85rem;
    border-bottom: 1px solid ${borderSoft};
    .title { flex: 1; font-family: ${mono}; font-size: 0.66rem; letter-spacing: 0.16em; text-transform: uppercase; color: var(--text-secondary); }
    .clear {
      background: transparent; border: 1px solid ${borderSoft}; color: var(--text-disabled);
      padding: 3px 7px; border-radius: 5px; cursor: pointer; font-size: 0.7rem;
      &:hover { color: var(--alert-error); border-color: rgba(239, 71, 111, 0.4); }
    }
  }
  .body {
    min-height: 320px; max-height: 60vh; overflow-y: auto;
    padding: 0.7rem; display: flex; flex-direction: column; gap: 0.55rem;
  }
  .empty { color: var(--text-disabled); font-size: 0.86rem; line-height: 1.55; }
  .suggest {
    display: block; width: 100%; text-align: left; background: transparent;
    border: 1px solid ${borderSoft}; color: var(--text-secondary);
    padding: 7px 11px; border-radius: 7px; font-size: 0.8rem; margin-top: 6px; cursor: pointer;
    &:hover { border-color: rgba(0, 180, 216, 0.4); background: rgba(0, 180, 216, 0.04); }
  }
  .msg { display: flex; gap: 8px; padding: 0 0.2rem;
    .avatar { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; background: rgba(11, 19, 32, 0.7); border: 1px solid ${borderSoft}; font-size: 0.7rem; color: var(--text-disabled); }
    &.assistant .avatar { color: ${accent}; }
    .bubble { background: rgba(11, 19, 32, 0.6); border: 1px solid ${borderSoft}; padding: 7px 11px; border-radius: 9px; color: var(--text-secondary); font-size: 0.86rem; line-height: 1.55; word-break: break-word; p, ul { margin: 3px 0; } }
    .pill { margin-top: 4px; display: inline-block; font-family: ${mono}; font-size: 0.6rem; letter-spacing: 0.12em; color: #62d59a; background: rgba(98, 213, 154, 0.07); border: 1px solid rgba(98, 213, 154, 0.3); padding: 2px 7px; border-radius: 4px; }
  }
  .input { display: flex; gap: 6px; padding: 0.6rem; border-top: 1px solid ${borderSoft};
    textarea { flex: 1; background: rgba(11, 19, 32, 0.6); border: 1px solid ${borderSoft}; border-radius: 8px; color: var(--text-primary); font-family: inherit; padding: 8px 11px; font-size: 0.86rem; resize: none; min-height: 40px; max-height: 120px; &:focus { outline: none; border-color: rgba(0, 180, 216, 0.5); } }
    button { width: 40px; height: 40px; border-radius: 7px; border: 1px solid rgba(0, 180, 216, 0.35); background: rgba(0, 180, 216, 0.15); color: ${accent}; cursor: pointer; display: flex; align-items: center; justify-content: center; &:disabled { opacity: 0.45; cursor: not-allowed; } &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.3); } }
  }
`;

/* ------------------------------ helpers ------------------------------ */

function formatTitleCase(name) {
  if (!name) return '';
  return name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

// Categorías para agrupar el contenido relacionado de TODA la plataforma.
const PLATFORM_CAT = [
  { id: 'investigaciones', label: 'Investigaciones', types: ['investigation_report', 'investigation_theory', 'investigation_connection', 'investigation_entity', 'investigation', 'insight'] },
  { id: 'medios', label: 'Medios y noticias', types: ['media_article', 'competitor_news'] },
  { id: 'sociales', label: 'Redes sociales', types: ['social_post', 'social_comment'] },
  { id: 'competidores', label: 'Competidores', types: ['competitor'] },
  { id: 'research', label: 'Research', types: ['research', 'research_run'] },
  { id: 'materiales', label: 'Materiales y discursos', types: ['material', 'speech', 'objective'] },
  { id: 'otros', label: 'Otros', types: [] },
];
function groupByCategory(items) {
  const typeToCat = {};
  for (const cat of PLATFORM_CAT) for (const t of cat.types) typeToCat[t] = cat.id;
  const buckets = {};
  for (const cat of PLATFORM_CAT) buckets[cat.id] = [];
  for (const it of (items || [])) buckets[typeToCat[it.source_type] || 'otros'].push(it);
  return PLATFORM_CAT.map((c) => ({ ...c, items: buckets[c.id] })).filter((c) => c.items.length > 0);
}

const SynthBox = styled.div`
  background: rgba(11, 19, 32, 0.5);
  border: 1px solid ${borderSoft};
  border-left: 2px solid ${accent};
  border-radius: 10px;
  padding: 1.1rem 1.35rem;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.65;
  h2 { color: var(--text-primary); font-size: 1.0rem; margin: 1.2rem 0 0.5rem; border-bottom: 1px solid ${borderSoft}; padding-bottom: 5px; }
  h2:first-child { margin-top: 0; }
  ul { margin: 0.3rem 0; padding-left: 20px; }
  li { margin: 3px 0; }
  strong { color: var(--text-primary); }
  a { color: ${accent}; word-break: break-word; }
  p { margin: 0.4rem 0; }
`;
const GenBtn = styled.button`
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(0, 180, 216, 0.15);
  border: 1px solid rgba(0, 180, 216, 0.4);
  color: ${accent};
  padding: 0.6rem 1.1rem;
  border-radius: 9px;
  font-size: 0.86rem;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover:not(:disabled) { background: rgba(0, 180, 216, 0.25); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

/* ------------------------------ chat panel --------------------------- */

function ChatPanel({ slug, name }) {
  const [history, setHistory] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const bodyRef = useRef(null);

  const loadChat = async () => {
    try { setHistory(await getPoliticianChatHistory(slug)); }
    catch (e) { console.warn('politician chat history err', e.message); }
  };
  useEffect(() => { loadChat(); /* eslint-disable-next-line */ }, [slug]);
  useEffect(() => { if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight; }, [history, sending]);

  const send = async (override) => {
    const q = (override ?? input).trim();
    if (!q || sending) return;
    setErr(null);
    setHistory((h) => [...h, { role: 'user', content: q, created_at: new Date().toISOString() }]);
    setInput('');
    setSending(true);
    try { await sendPoliticianChat(slug, q); await loadChat(); }
    catch (e) { setErr(friendlyError(e)); }
    finally { setSending(false); }
  };

  const clear = async () => {
    if (!window.confirm('¿Borrar historial del chat de este político?')) return;
    await clearPoliticianChatHistory(slug);
    setHistory([]);
  };

  const SUGGESTIONS = [
    `¿Hay denuncias o causas judiciales recientes contra ${name}?`,
    `Buscá en el HCD qué proyectos presentó o votó.`,
    `Resumime sus principales conflictos de interés y red empresarial.`,
  ];

  return (
    <ChatWrap>
      <div className="head">
        <FaRobot style={{ color: accent }} />
        <span className="title">Chat con IA</span>
        {history.length > 0 && (
          <button type="button" className="clear" onClick={clear} title="Borrar historial"><FaTrashAlt /></button>
        )}
      </div>
      <div className="body" ref={bodyRef}>
        {history.length === 0 && !sending && (
          <div className="empty">
            Indagá el perfil. La IA tiene acceso al perfil HCD, los dossiers donde aparece y el corpus indexado. Puede buscar evidencia en HCD y scrapear web.
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" className="suggest" onClick={() => send(s)}>{s}</button>
            ))}
          </div>
        )}
        {history.map((m) => (
          <div key={m.id || m.created_at} className={`msg ${m.role}`}>
            <div className="avatar">{m.role === 'user' ? <FaUser /> : <FaRobot />}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="bubble">
                {m.role === 'assistant'
                  ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                  : m.content}
              </div>
              {Array.isArray(m.actions) && m.actions.length > 0 && (
                <div className="pill">✓ {m.actions.filter((a) => a.ok).map((a) => a.tool).join(', ')}</div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="msg assistant">
            <div className="avatar"><FaRobot /></div>
            <div className="bubble" style={{ color: 'var(--text-disabled)' }}>investigando…</div>
          </div>
        )}
        {err && <ErrorBox>{err}</ErrorBox>}
      </div>
      <form className="input" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Indagá sobre este político…"
          disabled={sending}
          rows={1}
        />
        <button type="submit" disabled={sending || !input.trim()} aria-label="Enviar"><FaPaperPlane /></button>
      </form>
    </ChatWrap>
  );
}

/* ------------------------------ main page ---------------------------- */

export default function PoliticianProfilePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [tab, setTab] = useState('resumen');
  const [synth, setSynth] = useState(null);
  const [synthLoading, setSynthLoading] = useState(false);
  const [synthErr, setSynthErr] = useState(null);

  // Carga la síntesis guardada (la genera el cron diario), sin LLM.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await getPoliticianSynthesis(slug);
        if (!cancelled && r && r.synthesis) setSynth(r);
      } catch { /* sin guardada todavía */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Regenera ahora (RAG + LLM) y persiste.
  const genSynthesis = async () => {
    setSynthErr(null);
    setSynthLoading(true);
    try { setSynth(await generatePoliticianSynthesis(slug)); }
    catch (e) { setSynthErr(friendlyError(e)); }
    finally { setSynthLoading(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const p = await getPolitician(slug);
        if (!cancelled) setData(p);
      } catch (e) {
        if (!cancelled) setErr(friendlyError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const agg = useMemo(() => {
    const denuncias = [], conflictos = [], redEmp = [], redFam = [];
    for (const ep of (data?.entity_profiles || [])) {
      const p = ep.profile || {};
      for (const d of (p.denuncias_y_causas_judiciales || [])) denuncias.push(d);
      for (const c of (p.conflictos_de_interes || [])) conflictos.push(c);
      for (const r of (p.red_empresarial || [])) redEmp.push(r);
      for (const f of (p.red_familiar_y_designaciones || [])) redFam.push(f);
    }
    return { denuncias, conflictos, redEmp, redFam };
  }, [data]);

  const back = () => navigate('/investigations?tab=politicians');

  if (loading) return <Page><Back onClick={back}><FaArrowLeft /> Volver al directorio</Back><Loading>Cargando perfil…</Loading></Page>;
  if (err || !data) return <Page><Back onClick={back}><FaArrowLeft /> Volver al directorio</Back><ErrorBox>{err || 'No encontrado'}</ErrorBox></Page>;

  const hcd = data.hcd_profile || {};
  const bio = hcd.biografia || data.mentions?.[0]?.summary || null;
  const news = data.news || [];
  const relHcd = data.related_hcd || [];
  const relPlatform = data.related_platform || [];
  const relGroups = groupByCategory(relPlatform);
  const mentions = data.mentions || [];
  const connections = data.connections || [];
  const myEntityIds = new Set(mentions.map((m) => m.entity_id));

  const TABS = [
    { id: 'resumen', label: 'Resumen', icon: <FaIdBadge /> },
    { id: 'sintesis', label: 'Síntesis IA', icon: <FaBrain /> },
    { id: 'dossiers', label: 'Investigaciones', icon: <FaProjectDiagram />, count: mentions.length },
    { id: 'noticias', label: 'Noticias', icon: <FaNewspaper />, count: news.length },
    { id: 'hcd', label: 'HCD', icon: <FaLandmark />, count: relHcd.length },
    { id: 'relacionado', label: 'Relacionado', icon: <FaLayerGroup />, count: relPlatform.length },
    { id: 'red', label: 'Red', icon: <FaUsers />, count: connections.length },
    { id: 'chat', label: 'Chat IA', icon: <FaRobot /> },
  ];

  return (
    <Page>
      <Back onClick={back}><FaArrowLeft /> Volver al directorio</Back>

      <Hero>
        <div className="photo">
          {data.photo_url
            ? <img src={data.photo_url} alt={data.name} onError={(e) => { e.target.style.display = 'none'; }} />
            : <FaUserCircle />}
        </div>
        <div className="meta">
          <h1>{formatTitleCase(data.name)}</h1>
          {data.role && <div className="role">{data.role}</div>}
          {data.party && data.party !== 'NINGUNO' && <div className="party">{data.party}{data.period ? ` · ${data.period}` : ''}</div>}
          <div className="stats">
            <div className="stat"><span className="n">{mentions.length}</span><span className="l">Dossiers</span></div>
            <div className="stat"><span className="n">{connections.length}</span><span className="l">Conexiones</span></div>
            <div className="stat"><span className="n">{news.length}</span><span className="l">Noticias</span></div>
            <div className="stat"><span className="n">{relHcd.length}</span><span className="l">HCD ref.</span></div>
            {data.has_denuncias && (
              <div className="stat warn"><span className="n"><FaExclamationTriangle /></span><span className="l">Denuncias</span></div>
            )}
          </div>
        </div>
      </Hero>

      <TabBar>
        {TABS.map((t) => (
          <Tab key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.icon} {t.label}
            {typeof t.count === 'number' && t.count > 0 && <span className="count">{t.count}</span>}
          </Tab>
        ))}
      </TabBar>

      {/* RESUMEN */}
      {tab === 'resumen' && (
        <Panel>
          {bio && (<><SectionH><FaIdBadge /> Perfil</SectionH><Bio>{bio}</Bio></>)}

          {Array.isArray(hcd.trayectoria_resumen) && hcd.trayectoria_resumen.length > 0 && (
            <>
              <SectionH>Trayectoria</SectionH>
              <ul style={{ margin: 0, paddingLeft: 22, color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.6 }}>
                {hcd.trayectoria_resumen.slice(0, 12).map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </>
          )}

          {agg.denuncias.length > 0 && (
            <>
              <SectionH><FaBalanceScale /> Denuncias y causas ({agg.denuncias.length})</SectionH>
              <ListUl>
                {agg.denuncias.map((d, i) => (
                  <Risk key={i}>
                    <div className="meta">{d.fuente_tipo || 'denuncia'}{d.fecha ? ` · ${d.fecha}` : ''}{d.estado ? ` · ${d.estado}` : ''}</div>
                    {d.descripcion}
                  </Risk>
                ))}
              </ListUl>
            </>
          )}

          {agg.conflictos.length > 0 && (
            <>
              <SectionH>Conflictos de interés ({agg.conflictos.length})</SectionH>
              <ListUl>{agg.conflictos.map((c, i) => <Risk key={i} className="soft">{c.descripcion}</Risk>)}</ListUl>
            </>
          )}

          {agg.redEmp.length > 0 && (
            <>
              <SectionH><FaBuilding /> Red empresarial ({agg.redEmp.length})</SectionH>
              <ul style={{ margin: 0, paddingLeft: 22, color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.6 }}>
                {agg.redEmp.map((e, i) => <li key={i}><strong>{e.entidad}</strong>{e.rol ? ` — ${e.rol}` : ''}{e.actividad ? ` · ${e.actividad}` : ''}</li>)}
              </ul>
            </>
          )}

          {agg.redFam.length > 0 && (
            <>
              <SectionH>Red familiar / designaciones ({agg.redFam.length})</SectionH>
              <ul style={{ margin: 0, paddingLeft: 22, color: 'var(--text-secondary)', fontSize: '0.88rem', lineHeight: 1.6 }}>
                {agg.redFam.map((f, i) => <li key={i}><strong>{f.persona}</strong>{f.vinculo ? ` (${f.vinculo})` : ''}{f.cargo ? ` — ${f.cargo}` : ''}</li>)}
              </ul>
            </>
          )}

          {!bio && agg.denuncias.length === 0 && mentions.length === 0 && (
            <Empty>Este político fue cargado desde el HCD pero todavía no tiene perfil investigativo. Usá el Chat IA → puede crear un dossier o buscar denuncias.</Empty>
          )}
        </Panel>
      )}

      {/* SÍNTESIS IA */}
      {tab === 'sintesis' && (
        <Panel>
          {!synth && !synthLoading && !synthErr && (
            <div style={{ textAlign: 'center', padding: '1.6rem 1rem' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', maxWidth: 580, margin: '0 auto 1.1rem', lineHeight: 1.6 }}>
                Brief de inteligencia que cruza TODO el conocimiento de la plataforma sobre {formatTitleCase(data.name)}: perfil HCD, dossiers, denuncias, noticias y {relPlatform.length + relHcd.length} fragmentos relacionados (búsqueda semántica e5) → resumen ejecutivo generado por IA.
                <br /><span style={{ fontSize: '0.8rem', color: 'var(--text-disabled)' }}>Se genera automáticamente una vez al día. Todavía no hay una guardada — generala ahora.</span>
              </p>
              <GenBtn onClick={genSynthesis}><FaBrain /> Generar síntesis IA</GenBtn>
            </div>
          )}
          {synthLoading && <Loading>Analizando el corpus y redactando el brief…</Loading>}
          {synthErr && (
            <>
              <ErrorBox>{synthErr}</ErrorBox>
              <div style={{ marginTop: 10 }}><GenBtn onClick={genSynthesis}><FaSyncAlt /> Reintentar</GenBtn></div>
            </>
          )}
          {synth && !synthLoading && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.7rem', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <SectionH style={{ margin: 0 }}><FaBrain /> Síntesis de inteligencia</SectionH>
                  {synth.generated_at && (
                    <span style={{ fontFamily: mono, fontSize: '0.58rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-disabled)' }}>
                      Generada {formatDate(synth.generated_at)}
                    </span>
                  )}
                </div>
                <GenBtn onClick={genSynthesis} disabled={synthLoading} style={{ padding: '0.4rem 0.8rem', fontSize: '0.78rem' }}><FaSyncAlt /> Regenerar</GenBtn>
              </div>
              <SynthBox><ReactMarkdown remarkPlugins={[remarkGfm]}>{synth.synthesis || ''}</ReactMarkdown></SynthBox>
              {synth.stats && (
                <div style={{ fontFamily: mono, fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-disabled)', marginTop: 10 }}>
                  Basado en {synth.stats.rag_hcd} chunks HCD + {synth.stats.rag_plataforma} de plataforma · {synth.stats.dossiers} dossiers · {synth.stats.noticias} noticias
                </div>
              )}
            </>
          )}
        </Panel>
      )}

      {/* INVESTIGACIONES */}
      {tab === 'dossiers' && (
        <Panel>
          {mentions.length === 0 ? <Empty>No aparece en ningún dossier todavía.</Empty> : mentions.map((m) => (
            <CardLink key={m.entity_id} to={`/investigations/${m.investigation_slug}`}>
              <span className="swatch" style={{ background: m.cover_color || accent }} />
              <div className="tw">
                <strong>{m.investigation_title}</strong>
                {m.summary && <div className="resume">{m.summary}</div>}
              </div>
              <div className="badges">
                {m.has_denuncias && <span className="badge warn">denuncias</span>}
                {m.connection_count > 0 && <span className="badge conf">{m.connection_count} conex.</span>}
              </div>
            </CardLink>
          ))}
        </Panel>
      )}

      {/* NOTICIAS */}
      {tab === 'noticias' && (
        <Panel>
          {news.length === 0 ? <Empty>Sin noticias indexadas que mencionen a {formatTitleCase(data.name)}. El cron de noticias corre a diario.</Empty> : news.map((a) => (
            <ExtLink key={a.id} href={a.url} target="_blank" rel="noopener noreferrer">
              <FaNewspaper className="icon" />
              <div className="tw">
                <div className="title">{a.title}</div>
                {a.summary && <div className="sum">{a.summary}</div>}
                <div className="src">
                  {a.outlet_name && <span>{a.outlet_name}</span>}
                  {a.published_at && <span><FaRegClock style={{ marginRight: 4 }} />{formatDate(a.published_at)}</span>}
                </div>
              </div>
              <FaExternalLinkAlt className="ext" />
            </ExtLink>
          ))}
        </Panel>
      )}

      {/* HCD RELACIONADO */}
      {tab === 'hcd' && (
        <Panel>
          {relHcd.length === 0 ? <Empty>No se encontraron menciones en el corpus del HCD. (El índice se está re-embebiendo; volvé a probar en un rato.)</Empty> : relHcd.map((h) => (
            h.url
              ? <ExtLink key={h.id} href={h.url} target="_blank" rel="noopener noreferrer">
                  <FaLandmark className="icon" />
                  <div className="tw">
                    <div className="title">{h.source_label || h.source_type || 'HCD'}</div>
                    {h.content && <div className="sum">{h.content}</div>}
                  </div>
                  <FaExternalLinkAlt className="ext" />
                </ExtLink>
              : <div key={h.id} style={{ padding: '0.75rem 0.9rem', background: panelDeep, border: `1px solid ${borderSoft}`, borderRadius: 9, marginBottom: 7 }}>
                  <div style={{ color: 'var(--text-primary)', fontSize: '0.9rem', fontWeight: 600, display: 'flex', gap: 9, alignItems: 'center' }}>
                    <FaLandmark style={{ color: accent }} /> {h.source_label || h.source_type || 'HCD'}
                  </div>
                  {h.content && <div style={{ color: 'var(--text-disabled)', fontSize: '0.82rem', marginTop: 5, lineHeight: 1.5 }}>{String(h.content).slice(0, 400)}</div>}
                </div>
          ))}
        </Panel>
      )}

      {/* RELACIONADO (toda la plataforma) */}
      {tab === 'relacionado' && (
        <Panel>
          {relGroups.length === 0 ? (
            <Empty>Sin contenido relacionado en la plataforma todavía. Usa búsqueda semántica e5 sobre reportes, teorías, noticias, redes, competidores y research.</Empty>
          ) : relGroups.map((g) => (
            <div key={g.id}>
              <SectionH><FaLayerGroup /> {g.label} ({g.items.length})</SectionH>
              {g.items.map((h) => (
                h.url ? (
                  <ExtLink key={`${h.table}:${h.id}`} href={h.url} target="_blank" rel="noopener noreferrer">
                    <FaLayerGroup className="icon" />
                    <div className="tw">
                      <div className="title">{h.source_label || h.source_type}</div>
                      {h.content && <div className="sum">{String(h.content).slice(0, 240)}</div>}
                    </div>
                    <FaExternalLinkAlt className="ext" />
                  </ExtLink>
                ) : (
                  <div key={`${h.table}:${h.id}`} style={{ padding: '0.7rem 0.9rem', background: panelDeep, border: `1px solid ${borderSoft}`, borderRadius: 9, marginBottom: 7 }}>
                    <div style={{ color: 'var(--text-primary)', fontSize: '0.88rem', fontWeight: 600, display: 'flex', gap: 9, alignItems: 'center' }}>
                      <FaLayerGroup style={{ color: accent, flexShrink: 0 }} /> {h.source_label || h.source_type}
                    </div>
                    {h.content && <div style={{ color: 'var(--text-disabled)', fontSize: '0.8rem', marginTop: 4, lineHeight: 1.45 }}>{String(h.content).slice(0, 240)}</div>}
                  </div>
                )
              ))}
            </div>
          ))}
        </Panel>
      )}

      {/* RED */}
      {tab === 'red' && (
        <Panel>
          {connections.length === 0 ? <Empty>Sin conexiones registradas en los dossiers.</Empty> : connections.map((c, i) => {
            const isFrom = myEntityIds.has(c.from_entity_id);
            const otherName = isFrom ? c.to_name : c.from_name;
            const kc = KIND_COLOR[c.kind] || '#6b7280';
            return (
              <ConnRow key={i}>
                <span className="arr">↔</span>
                <span className="name">{otherName}</span>
                <span className="kind" style={{ color: kc, borderColor: `${kc}66`, background: `${kc}15` }}>{c.kind}</span>
                {c.label && <span className="desc">{c.label}</span>}
              </ConnRow>
            );
          })}
        </Panel>
      )}

      {/* CHAT */}
      {tab === 'chat' && (
        <Panel><ChatPanel slug={slug} name={data.name} /></Panel>
      )}
    </Page>
  );
}
